import React from 'react';
import { Box } from '@interstate/components/Box';
import { List, ListItem } from '@interstate/components/List';
import { CheckBox } from '@interstate/components/CheckBox';
import { TextInput } from '@interstate/components/TextInput';
import { SelectInput } from '@interstate/components/SelectInput';
import type { DollarValueRounding, FontSizeAdjustment, DisplayItemKey, DisplayConfigState } from './constants';
import { TEXT_INPUT_MAX_LENGTH, DOLLAR_VALUE_ROUNDING_OPTIONS, DOLLAR_VALUE_ROUNDING } from './constants';
import { getDisplayLabel, hasDisplayName } from './utils';
import ConditionalDiscount from './components/ConditionalDiscount';
import CustomDisclaimerField from './components/CustomDisclaimerField';
import FontSizeAdjustmentField from './components/FontSizeAdjustmentField';
import PaymentRangeSection from './components/PaymentRangeSection';

interface DisplayTabProps {
    displayConfiguration: DisplayConfigState;
    onDisplayActiveChange: (displayKey: DisplayItemKey, checked: boolean) => void;
    onDisplayValueChange: (displayKey: DisplayItemKey, newDisplayValue: string | undefined) => void;
    onDisplayNameChange: (displayKey: DisplayItemKey, newDisplayName: string) => void;
    onDollarValueRoundingChange: (value: DollarValueRounding) => void;
    onFontSizeAdjustmentChange: (value: FontSizeAdjustment) => void;
}

const DisplayTabBase: React.FC<DisplayTabProps> = ({
    displayConfiguration,
    onDisplayActiveChange,
    onDisplayNameChange,
    onDisplayValueChange,
    onDollarValueRoundingChange,
    onFontSizeAdjustmentChange
}) => {
    const { dollarValueRounding, conditionalDiscount, customDisclaimer, fontSizeAdjustment, paymentRange, ...displayItems } =
        displayConfiguration;
    const dollarValueRoundingValue = dollarValueRounding?.value ?? DOLLAR_VALUE_ROUNDING.ROUND_TO_NEAREST_DOLLAR;

    const listItems = [
        ...(paymentRange
            ? [
                  <PaymentRangeSection
                      key="paymentRange"
                      isActive={paymentRange.active}
                      value={paymentRange.value}
                      onDisplayActiveChange={onDisplayActiveChange}
                      onDisplayValueChange={onDisplayValueChange}
                  />
              ]
            : []),
        ...(fontSizeAdjustment
            ? [
                  <FontSizeAdjustmentField
                      key="fontSizeAdjustment"
                      value={fontSizeAdjustment.value}
                      onFontSizeAdjustmentChange={onFontSizeAdjustmentChange}
                  />
              ]
            : []),

        ...(customDisclaimer
            ? [
                  <CustomDisclaimerField
                      key={'customDisclaimer-key'}
                      isActive={customDisclaimer.active}
                      value={customDisclaimer.value ?? ''}
                      onDisplayActiveChange={onDisplayActiveChange}
                      onDisplayValueChange={onDisplayValueChange}
                  />
              ]
            : []),
        ...(dollarValueRounding
            ? [
                  <ListItem key="dollarValueRounding" id="dollarValueRounding">
                      <Box sx={{ width: '50%', py: 1 }}>
                          <SelectInput
                              label="Dollar value rounding"
                              name="dollarValueRounding"
                              value={dollarValueRoundingValue}
                              options={DOLLAR_VALUE_ROUNDING_OPTIONS.map((opt) => ({ label: opt.label, value: opt.value }))}
                              onChange={(e) => onDollarValueRoundingChange(e.target.value as DollarValueRounding)}
                              displayDeselectOption={false}
                              data-testid="dollar-value-rounding-select"
                              sx={{
                                  '& .MuiInputLabel-root.MuiInputLabel-root': {
                                      color: '#000000'
                                  }
                              }}
                          />
                      </Box>
                  </ListItem>
              ]
            : []),
        ...(conditionalDiscount
            ? [
                  <ConditionalDiscount
                      key="conditionalDiscount"
                      isActive={conditionalDiscount.active}
                      value={conditionalDiscount.value}
                      onDisplayValueChange={onDisplayValueChange}
                      onDisplayActiveChange={onDisplayActiveChange}
                  />
              ]
            : []),
        ...Object.entries(displayItems).map(([key, config]) => {
            const displayKey = key as DisplayItemKey;

            return (
                <ListItem key={key} id={key}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            width: '100%',
                            height: '64px'
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px'
                            }}
                        >
                            <CheckBox
                                value={key}
                                checked={config.active}
                                onChange={(e) => onDisplayActiveChange(displayKey, e.target.value.checked)}
                                data-testid={`display-tab-checkbox-${key}`}
                            />
                            <Box>{getDisplayLabel(key)}</Box>
                        </Box>
                        {hasDisplayName(config) && (
                            <TextInput
                                value={config.displayName}
                                name={key}
                                displayLabel={false}
                                onChange={(e) => onDisplayNameChange(displayKey, e.target.value)}
                                placeholder="Display Name"
                                required
                                maxLength={TEXT_INPUT_MAX_LENGTH}
                                customErrorMessages={{
                                    exceedsMaxLength: `Value exceeds ${TEXT_INPUT_MAX_LENGTH} characters.`
                                }}
                                data-testid={`display-tab-name-input-${key}`}
                                sx={{
                                    width: '100%',
                                    maxWidth: '250px',
                                    position: 'relative',
                                    top: '8px',
                                    alignSelf: 'flex-start',
                                    caretColor: 'auto'
                                }}
                            />
                        )}
                    </Box>
                </ListItem>
            );
        })
    ];

    return (
        <Box sx={{ pt: 2, pb: 2, ml: -1 }}>
            <List>{listItems}</List>
        </Box>
    );
};

export const DisplayTab = React.memo(DisplayTabBase);
